import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Clock3, FileText, PackageCheck, Play, Send, ShieldAlert } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import './OrderDetail.css';

const yuan = cents => `¥${((Number(cents) || 0) / 100).toFixed(2)}`;

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const emptyDisputeForm = {
  reason: '',
  detail: '',
  evidence: '',
};

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const text = useMemo(() => (isZh ? {
    back: '返回财务中心',
    title: '订单详情',
    orderNumber: '订单号',
    placedAt: '下单时间',
    service: '服务 / 需求',
    requirements: '需求说明',
    deadline: '交付截止',
    package: '订单套餐',
    preferredTime: '期望服务时间',
    noRequirements: '暂无额外需求说明。',
    buyer: '买家',
    seller: '卖家',
    amount: '订单金额',
    platformFee: '平台服务费',
    sellerSettlement: '卖家结算',
    status: '当前状态',
    buyerView: '买家进度',
    sellerView: '卖家进度',
    placed: '已下单',
    received: '收到订单',
    started: '开始工作',
    delivered: '已交付',
    completed: '已完成',
    pending: '待完成',
    noTime: '尚未发生',
    deliveryNote: '交付说明',
    noDeliveryNote: '卖家暂未填写交付说明。',
    actions: '订单操作',
    startWork: '开始工作',
    starting: '开始中...',
    deliverWork: '提交交付',
    delivering: '提交中...',
    deliveryPlaceholder: '说明本次交付的内容、文件或后续安排',
    release: '确认收货并放款',
    releasing: '处理中...',
    releaseHint: '确认后，托管款会按当前费率结算给卖家。',
    dispute: '发起争议',
    refund: '申请退款',
    cancel: '取消',
    reason: '原因',
    detail: '补充说明',
    evidence: '证据链接（每行一个）',
    submitDispute: '提交争议',
    submitRefund: '提交退款申请',
    submitting: '提交中...',
    disputeOpen: '该订单存在处理中争议，暂不能继续推进。',
    refundOpen: '退款处理中',
    disputeSuccess: '申请已提交，等待平台处理。',
    releaseSuccess: '订单已完成，款项已结算给卖家。',
    startSuccess: '已开始工作。',
    deliverSuccess: '已提交交付，等待买家验收。',
    notFound: '订单不存在或无权查看。',
    loading: '加载订单中...',
    failed: '操作失败',
    inProgress: '进行中',
    newOrder: '新订单',
    awaitingAcceptance: '待验收',
    completedStatus: '已完成',
    refundedStatus: '退款中',
    pendingPayment: '待托管付款',
    escrow: '托管中',
  } : {
    back: 'Back to finance',
    title: 'Order details',
    orderNumber: 'Order number',
    placedAt: 'Placed',
    service: 'Service / task',
    requirements: 'Requirements',
    deadline: 'Deadline',
    package: 'Package',
    preferredTime: 'Preferred time',
    noRequirements: 'No additional requirements were provided.',
    buyer: 'Client',
    seller: 'Provider',
    amount: 'Order amount',
    platformFee: 'Platform fee',
    sellerSettlement: 'Provider settlement',
    status: 'Current status',
    buyerView: 'Client progress',
    sellerView: 'Provider progress',
    placed: 'Placed',
    received: 'Order received',
    started: 'Work started',
    delivered: 'Delivered',
    completed: 'Completed',
    pending: 'Pending',
    noTime: 'Not yet',
    deliveryNote: 'Delivery note',
    noDeliveryNote: 'The provider has not added a delivery note.',
    actions: 'Order actions',
    startWork: 'Start work',
    starting: 'Starting...',
    deliverWork: 'Submit delivery',
    delivering: 'Submitting...',
    deliveryPlaceholder: 'Describe the delivered work, files, or next steps',
    release: 'Confirm delivery and release',
    releasing: 'Processing...',
    releaseHint: 'Confirming releases the escrowed amount to the provider at the current fee rate.',
    dispute: 'Open dispute',
    refund: 'Request refund',
    cancel: 'Cancel',
    reason: 'Reason',
    detail: 'Details',
    evidence: 'Evidence links (one per line)',
    submitDispute: 'Submit dispute',
    submitRefund: 'Submit refund request',
    submitting: 'Submitting...',
    disputeOpen: 'This order has an open dispute and cannot move forward.',
    refundOpen: 'Refund in review',
    disputeSuccess: 'Your request was submitted for platform review.',
    releaseSuccess: 'The order is complete and the provider has been settled.',
    startSuccess: 'Work has started.',
    deliverSuccess: 'Delivery was submitted for client acceptance.',
    notFound: 'The order does not exist or you do not have access.',
    loading: 'Loading order...',
    failed: 'Operation failed',
    inProgress: 'In progress',
    newOrder: 'New order',
    awaitingAcceptance: 'Awaiting acceptance',
    completedStatus: 'Completed',
    refundedStatus: 'Refund in review',
    pendingPayment: 'Pending escrow payment',
    escrow: 'In escrow',
  }), [isZh]);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [activeForm, setActiveForm] = useState('');
  const [disputeForm, setDisputeForm] = useState(emptyDisputeForm);
  const [disputes, setDisputes] = useState([]);

  const loadOrder = useCallback(() => {
    setLoading(true);
    setError('');
    return Promise.allSettled([
      api.get(`/payments/orders/${id}`),
      api.get('/payments/disputes'),
    ])
      .then(([orderResult, disputesResult]) => {
        if (orderResult.status === 'rejected') throw orderResult.reason;
        setOrder(orderResult.value.data);
        setDeliveryNote(orderResult.value.data.delivery_note || '');
        if (disputesResult.status === 'fulfilled') setDisputes(disputesResult.value.data.disputes || []);
      })
      .catch(err => {
        setOrder(null);
        setError(err.response?.data?.error || text.notFound);
      })
      .finally(() => setLoading(false));
  }, [id, text.notFound]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const isBuyer = order && String(order.buyer_id) === String(user?.id);
  const isSeller = order && String(order.seller_id) === String(user?.id);
  const hasOpenDispute = disputes.some(item => Number(item.order_id) === Number(order?.id) && item.status === 'open');
  const activeDispute = disputes.find(item => Number(item.order_id) === Number(order?.id) && item.status === 'open');
  const isRefundRequest = /退款|refund/i.test(String(activeDispute?.reason || ''));
  const statusLabel = useMemo(() => ({
    pending_payment: text.pendingPayment,
    escrow: text.escrow,
    completed: text.completedStatus,
    refunded: text.refundedStatus,
    refund_pending: text.refundedStatus,
    in_progress: text.inProgress,
    awaiting_acceptance: text.awaitingAcceptance,
    new_order: text.newOrder,
    delivered: text.delivered,
  }[isRefundRequest ? 'refund_pending' : (isSeller ? order?.seller_status : order?.buyer_status)] || order?.status || text.inProgress), [isRefundRequest, isSeller, order, text]);

  const timelineLabel = key => ({
    placed: text.placed,
    received: text.received,
    started: text.started,
    delivered: text.delivered,
    completed: text.completed,
  }[key] || key);

  const orderEndpoints = {
    start: `/payments/orders/${id}/start`,
    deliver: `/payments/orders/${id}/deliver`,
    release: `/payments/orders/${id}/release`,
    disputes: `/payments/orders/${id}/disputes`,
    refund: `/payments/orders/${id}/refund`,
  };

  const submitAction = async (action, successMessage, payload) => {
    setWorking(action);
    setNotice('');
    setError('');
    try {
      const response = await api.post(orderEndpoints[action], payload);
      if (response.data?.order) setOrder(response.data.order);
      setNotice(successMessage);
      setActiveForm('');
      if (action === 'disputes' || action === 'refund') {
        const disputeResponse = await api.get('/payments/disputes');
        setDisputes(disputeResponse.data.disputes || []);
      }
      return response;
    } catch (err) {
      setError(err.response?.data?.error || text.failed);
      return null;
    } finally {
      setWorking('');
    }
  };

  const startWork = () => submitAction('start', text.startSuccess);

  const deliverWork = event => {
    event.preventDefault();
    return submitAction('deliver', text.deliverSuccess, { note: deliveryNote });
  };

  const releaseOrder = () => submitAction('release', text.releaseSuccess);

  const submitDispute = event => {
    event.preventDefault();
    const evidence = disputeForm.evidence
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);
    const action = activeForm === 'refund' ? 'refund' : 'disputes';
    return submitAction(action, action === 'refund' ? text.disputeSuccess : text.disputeSuccess, {
      reason: disputeForm.reason,
      detail: disputeForm.detail,
      evidence,
    }).then(() => {
      setDisputeForm(emptyDisputeForm);
    });
  };

  const orderTimeline = order ? order.timeline : null;
  const timeline = orderTimeline?.[isSeller ? 'seller' : 'buyer'] || [];
  const latestReached = timeline.reduce((latest, item, index) => (item.timestamp ? index : latest), -1);

  if (loading) {
    return <div className="order-detail-page"><div className="order-detail-shell"><div className="order-detail-loading">{text.loading}</div></div></div>;
  }

  if (!order) {
    return (
      <div className="order-detail-page">
        <div className="order-detail-shell">
          <Link className="order-back-link" to="/wallet"><ArrowLeft size={16} />{text.back}</Link>
          <div className="order-detail-empty">{error || text.notFound}</div>
        </div>
      </div>
    );
  }

  const canStart = isSeller && order.status === 'escrow' && !order.started_at && !hasOpenDispute;
  const canDeliver = isSeller && order.status === 'escrow' && Boolean(order.started_at) && !order.delivered_at && !hasOpenDispute;
  const canRelease = isBuyer && order.status === 'escrow' && !hasOpenDispute;
  const canDispute = (isBuyer || isSeller) && order.status === 'escrow' && !hasOpenDispute;

  return (
    <div className="order-detail-page">
      <div className="order-detail-shell">
        <div className="order-detail-topbar">
          <Link className="order-back-link" to="/wallet"><ArrowLeft size={16} />{text.back}</Link>
          <span className="order-detail-kicker">{text.title}</span>
        </div>

        <section className="order-detail-hero">
          <div>
            <div className="order-detail-eyebrow">{text.orderNumber} #{order.order_no || order.id}</div>
            <h1>{order.task_title || order.service_title || `${text.service} #${order.task_id || order.id}`}</h1>
            <p>{text.placedAt} {formatDate(order.placed_at || order.created_at)}</p>
          </div>
          <span className={`order-status order-status-${order.status}`}>{statusLabel}</span>
        </section>

        {hasOpenDispute && <div className="order-dispute-banner"><ShieldAlert size={18} /><span>{isRefundRequest ? text.refundOpen : text.disputeOpen}</span></div>}
        {notice && <div className="order-notice order-notice-success">{notice}</div>}
        {error && <div className="order-notice order-notice-error">{error}</div>}

        <div className="order-detail-grid">
          <main className="order-detail-main">
            <section className="order-detail-panel order-summary-panel">
              <div className="order-panel-heading">
                <div>
                  <span className="order-panel-eyebrow">{text.service}</span>
                  <h2>{order.service_title || order.task_title || '-'}</h2>
                </div>
                <PackageCheck size={22} />
              </div>
              <div className="order-people-grid">
                <div><span>{text.buyer}</span><strong>{order.buyer_name || `#${order.buyer_id}`}</strong></div>
                <div><span>{text.seller}</span><strong>{order.seller_name || `#${order.seller_id}`}</strong></div>
              </div>
                  <div className="order-money-grid">
                <div><span>{text.amount}</span><strong>{yuan(order.amount)}</strong></div>
                <div><span>{text.platformFee} ({Number(order.platform_fee_rate || 5)}%)</span><strong>- {yuan(order.platform_fee_amount)}</strong></div>
                <div><span>{text.sellerSettlement}</span><strong>{yuan(order.seller_amount)}</strong></div>
                  </div>
                </section>

                <section className="order-detail-panel">
                  <div className="order-panel-heading">
                    <div>
                      <span className="order-panel-eyebrow">{text.requirements}</span>
                      <h2>{order.package_snapshot?.name || order.task_title || '-'}</h2>
                    </div>
                    <FileText size={22} />
                  </div>
                  <div className="order-requirements">
                    <p>{order.task_description || text.noRequirements}</p>
                    <div className="order-requirement-meta">
                      {order.package_snapshot?.name && <span><strong>{text.package}</strong>{order.package_snapshot.name}</span>}
                      {order.booking_preferred_time && <span><strong>{text.preferredTime}</strong>{order.booking_preferred_time}</span>}
                      {order.task_deadline && <span><strong>{text.deadline}</strong>{order.task_deadline}</span>}
                    </div>
                  </div>
                </section>

            <section className="order-detail-panel">
              <div className="order-panel-heading">
                <div>
                  <span className="order-panel-eyebrow">{isSeller ? text.sellerView : text.buyerView}</span>
                  <h2>{text.status}</h2>
                </div>
                <Clock3 size={22} />
              </div>
              <div className={`order-timeline order-timeline-${timeline.length}`}>
                {timeline.map((item, index) => {
                  const reached = Boolean(item.timestamp);
                  const current = reached && index === latestReached && order.status !== 'completed';
                  return (
                    <div className={`order-timeline-step ${reached ? 'is-reached' : ''} ${current ? 'is-current' : ''}`} key={item.key}>
                      <div className="order-timeline-track" />
                      <div className="order-timeline-node">{reached ? <Check size={15} /> : <span>{index + 1}</span>}</div>
                      <strong>{timelineLabel(item.key)}</strong>
                      <small>{reached ? formatDate(item.timestamp) : text.noTime}</small>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="order-detail-panel">
              <div className="order-panel-heading">
                <div>
                  <span className="order-panel-eyebrow">{text.deliveryNote}</span>
                  <h2>{order.delivered_at ? text.delivered : text.pending}</h2>
                </div>
                <FileText size={22} />
              </div>
              <p className="order-delivery-note">{order.delivery_note || text.noDeliveryNote}</p>
            </section>
          </main>

          <aside className="order-detail-sidebar">
            <section className="order-detail-panel order-actions-panel">
              <div className="order-panel-heading">
                <div>
                  <span className="order-panel-eyebrow">{text.actions}</span>
                  <h2>{isSeller ? text.seller : text.buyer}</h2>
                </div>
                {isSeller ? <Send size={22} /> : <ShieldAlert size={22} />}
              </div>

              {canStart && (
                <button className="btn btn-primary order-action-button" type="button" disabled={working === 'start'} onClick={startWork}>
                  <Play size={16} />{working === 'start' ? text.starting : text.startWork}
                </button>
              )}

              {canDeliver && activeForm !== 'deliver' && (
                <button className="btn btn-primary order-action-button" type="button" onClick={() => setActiveForm('deliver')}>
                  <Send size={16} />{text.deliverWork}
                </button>
              )}

              {activeForm === 'deliver' && (
                <form className="order-action-form" onSubmit={deliverWork}>
                  <label>{text.deliveryNote}<textarea rows={5} value={deliveryNote} placeholder={text.deliveryPlaceholder} onChange={event => setDeliveryNote(event.target.value)} /></label>
                  <div className="order-form-actions">
                    <button className="btn btn-primary" type="submit" disabled={working === 'deliver'}>{working === 'deliver' ? text.delivering : text.deliverWork}</button>
                    <button className="btn btn-ghost" type="button" onClick={() => setActiveForm('')}>{text.cancel}</button>
                  </div>
                </form>
              )}

              {canRelease && (
                <div className="order-release-block">
                  <button className="btn btn-primary order-action-button" type="button" disabled={working === 'release'} onClick={releaseOrder}>
                    <PackageCheck size={16} />{working === 'release' ? text.releasing : text.release}
                  </button>
                  <p>{text.releaseHint}</p>
                </div>
              )}

              {canDispute && activeForm !== 'dispute' && activeForm !== 'refund' && (
                <div className="order-secondary-actions">
                  <button className="btn btn-outline order-action-button" type="button" onClick={() => setActiveForm('dispute')}>
                    <ShieldAlert size={16} />{text.dispute}
                  </button>
                      {isBuyer && order.delivered_at && <button className="btn btn-ghost order-action-button" type="button" onClick={() => setActiveForm('refund')}>{text.refund}</button>}
                </div>
              )}

              {(activeForm === 'dispute' || activeForm === 'refund') && (
                <form className="order-action-form" onSubmit={submitDispute}>
                  <label>{text.reason}<input required maxLength={120} value={disputeForm.reason} onChange={event => setDisputeForm(form => ({ ...form, reason: event.target.value }))} /></label>
                  <label>{text.detail}<textarea rows={4} maxLength={2000} value={disputeForm.detail} onChange={event => setDisputeForm(form => ({ ...form, detail: event.target.value }))} /></label>
                  <label>{text.evidence}<textarea rows={3} value={disputeForm.evidence} onChange={event => setDisputeForm(form => ({ ...form, evidence: event.target.value }))} /></label>
                  <div className="order-form-actions">
                    <button className="btn btn-primary" type="submit" disabled={working === 'disputes' || working === 'refund'}>{working ? text.submitting : activeForm === 'refund' ? text.submitRefund : text.submitDispute}</button>
                    <button className="btn btn-ghost" type="button" onClick={() => setActiveForm('')}>{text.cancel}</button>
                  </div>
                </form>
              )}

              {!canStart && !canDeliver && !canRelease && !canDispute && !hasOpenDispute && order.status !== 'escrow' && (
                <div className="order-action-muted">{statusLabel}</div>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
